import { useEffect, useState } from 'react';

export type WireStyle = 'bezier' | 'ortho';

export const WIRE_STYLES: readonly WireStyle[] = ['bezier', 'ortho'] as const;

/** Default wire style. Mirrors the handoff prototype default. */
const DEFAULT_WIRE_STYLE: WireStyle = 'ortho';

const STORAGE_KEY = 'process-editor:wire-style';

const isWireStyle = (v: unknown): v is WireStyle =>
  v === 'bezier' || v === 'ortho';

const readPersisted = (): WireStyle => {
  // Guard against SSR / test environments where `localStorage` may
  // throw the moment we touch it (private mode, sandboxed iframes).
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (isWireStyle(raw)) return raw;
  } catch {
    /* ignore -- fall through to default */
  }
  return DEFAULT_WIRE_STYLE;
};

const writePersisted = (style: WireStyle): void => {
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, style);
  } catch {
    /* ignore -- persistence is best-effort */
  }
};

export interface UseWireStyleReturn {
  readonly wireStyle: WireStyle;
  readonly setWireStyle: (next: WireStyle) => void;
}

/**
 * Per-user wire-style preference, persisted to {@code localStorage} so
 * the choice survives a page reload. Drives the {@code BranchedEdge}
 * renderer's path generator and the bottom-toolbar's bezier/ortho
 * toggle.
 *
 * The hook lazy-reads {@code localStorage} only once on mount; reads
 * inside the lazy initializer so the parse cost doesn't run on every
 * re-render.
 */
export function useWireStyle(): UseWireStyleReturn {
  const [wireStyle, setWireStyle] = useState<WireStyle>(() => readPersisted());

  useEffect(() => {
    writePersisted(wireStyle);
  }, [wireStyle]);

  return { wireStyle, setWireStyle };
}
